"use client"

import { ScreenNames, ScreenPaths } from "config/routing"
import { redirect, usePathname } from "next/navigation"
import type React from "react"
import { useEffect } from "react"
import { handleChurnZeroReporting } from "services"
import type { User } from "types"

export interface StateProviderProps {
    children: React.ReactNode
    user: User
}

/**
 * UserAccessWrapper component is used to control user access based on specific user properties.
 *
 * This component currently handles the scenario where a user does not belong to any organization.
 * If the user does not have an associated organization, they will be redirected to the New Organization screen.
 *
 * Intended to be used as a transition piece until the user organization is handled via Clerk & Clerk Organizations
 *
 * @component
 * @param {StateProviderProps} props - The properties for the UserAccessWrapper component.
 * @param {React.ReactNode} props.children - The child components to be rendered within the UserAccessWrapper.
 * @param {User} props.user - The user object containing user details.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * - This component uses the `usePathname` hook from Next.js to get the current pathname.
 * - The redirection logic is implemented within a `useEffect` hook to handle the user without an organization.
 * - The redirection is triggered if the user does not have an organization and the current pathname does not include the welcome screen.
 *
 * @example
 * ```tsx
 * <UserAccessWrapper user={currentUser}>
 *   <YourComponent />
 * </UserAccessWrapper>
 * ```
 */
export default function UserAccessWrapper({ children, user }: StateProviderProps) {
    const pathname = usePathname()

    useEffect(() => {
        if (typeof window !== "undefined") {
            handleChurnZeroReporting(user?.contact?.email, user?.organization?.organizationId)

            if (window.heap) {
                window.heap = window.heap
                window.heap.identify(user?.id)
                window.heap.addUserProperties({
                    email: user?.contact?.email,
                    organizationId: user.organization?.organizationId,
                    organizationName: user.organization?.organizationId,
                    isBlxUser: user?.trustedUser || user?.contact?.email.includes("@brewlogix.com"),
                    orgRole: user?.brewlogixSuperadmin ? "superadmin" : user?.organization?.role
                })
            }
        }
        // Handle User without Organization
        // [ENG-3526] - Handle user with no org gracefully
        // https://app.clickup.com/t/9014082925/ENG-3526
        if (user && !user.organization) {
            if (!pathname.includes(ScreenNames.Welcome)) {
                redirect(ScreenPaths.NewOrganization)
            }
        }
    }, [pathname, user])

    return <>{children}</>
}
